import { useEffect, useRef } from 'react';

import * as Comlink from 'comlink';

import { createCanvasElement } from '@src/helpers';
import type { VirtualBackgroundWorkerType } from '@src/workers/virtual-background.worker';

// Interface for the animation frame processor
interface IFrameProcessor {
  stop: () => void;
}

/**
 * Video Background Hook
 *
 * Processes video/animation files as virtual backgrounds. This hook is used internally
 * by useVirtualBackgroundHandler to handle video background types.
 *
 * **Purpose:**
 * - Loads and plays video/animation files for use as virtual backgrounds
 * - Sends video frames to the worker for processing as background replacement
 * - Manages video element lifecycle and canvas rendering
 *
 * **How It Works:**
 * 1. Creates a hidden video element to play the background video/animation
 * 2. Draws video frames to a canvas at specified FPS
 * 3. Sends canvas frames to worker for background processing
 * 4. Handles retries for video loading and initialization
 *
 * **Performance Optimizations:**
 * - Capped FPS at 15 for animations (sufficient for background, saves CPU)
 * - Reusable canvas element
 * - Proper cleanup of blob URLs and timer workers
 *
 * @param {string} videoSrc - Source URL of the video/animation file
 * @param {Remote<VirtualBackgroundWorkerType> | null} workerApi - Worker API reference
 * @param {boolean} isMobileDevice - Whether running on mobile device
 * @param {number} fps - Target frames per second for background processing
 *
 * @internal This hook is internal to useVirtualBackgroundHandler
 */
const useVideoBackground = (
  videoUrl: string,
  VirtualBackgroundWorker: Comlink.Remote<VirtualBackgroundWorkerType> | null,
  isMobileDevice: boolean = false,
  targetFPS: number = 15, // Reduced from 20 - animations don't need high FPS
  target: 'active' | 'preview' = 'active'
) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const activeVideoUrl = useRef<string | null>(null);
  const processorRef = useRef<IFrameProcessor | null>(null);
  const retryCountRef = useRef(0);
  const timerWorkerBlobUrlRef = useRef<string | null>(null); // MEMORY: Track blob URL for cleanup
  const maxRetries = 150; // Approx 10s at 15fps - plenty of time for initial load

  useEffect(() => {
    if (!videoUrl || !VirtualBackgroundWorker || videoUrl === activeVideoUrl.current) return;
    activeVideoUrl.current = videoUrl;
    retryCountRef.current = 0; // Reset retries for new video

    const video = document.createElement('video');
    videoRef.current = video;
    video.src = videoUrl;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.crossOrigin = 'anonymous';

    let isMounted = true;

    const processVideoFrame = async () => {
      if (!videoRef.current || !canvasRef.current || !VirtualBackgroundWorker) {
        return;
      }

      const ctx = canvasRef.current.getContext('2d');
      if (!ctx || videoRef.current.paused || videoRef.current.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        return;
      }

      // MEMORY LEAK FIX: Ensure bitmap/frame is always closed even on error
      let frame: ImageBitmap | VideoFrame | null = null;
      try {
        // PERFORMANCE OPTIMIZATION: Use VideoFrame for zero-copy transfer if available
        const VideoFrameConstructor = (globalThis as any).VideoFrame;

        if (VideoFrameConstructor) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          frame = new VideoFrameConstructor(videoRef.current) as any;
        } else {
          // Fallback: Draw to canvas and create bitmap
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          ctx.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);
          frame = await createImageBitmap(canvasRef.current);
        }

        if (!frame) {
          return;
        }

        if (!VirtualBackgroundWorker) {
          frame.close();
          return;
        }

        if (target === 'active') {
          await VirtualBackgroundWorker.setVideoBackgroundFrame(Comlink.transfer(frame as any, [frame as any]));
          frame = null; // Ownership transferred
        } else {
          // Check if setPreviewVideoBackgroundFrame exists
          if ((VirtualBackgroundWorker as any).setPreviewVideoBackgroundFrame) {
            await VirtualBackgroundWorker.setPreviewVideoBackgroundFrame(
              Comlink.transfer(frame as any, [frame as any])
            );
            frame = null; // Ownership transferred
          }
        }

        retryCountRef.current = 0; // Reset on success
      } catch (error) {
        // Only log non-InvalidStateError (InvalidStateError is expected while video is loading)
        if (!(error instanceof DOMException && error.name === 'InvalidStateError')) {
          console.error('Video background error:', error);
        }

        if (retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          // Reduce log spam: only log every 10 retries
          if (retryCountRef.current % 10 === 0) {
            console.warn(`Video background loading... (${retryCountRef.current}/${maxRetries})`);
          }
        } else {
          console.error('Video background failed to load after max retries. Stopping processor.');
          processorRef.current?.stop();
          // Do not reset retryCountRef - effectively stops the loop processing for this video
        }
      } finally {
        // OWNERSHIP TRANSFER SAFEGUARD:
        // If frame is still set here, it means transfer failed or wasn't attempted.
        // We MUST close it to prevent memory leaks, especially for ImageBitmap/VideoFrame.
        if (frame) {
          try {
            frame.close();
          } catch (e) {
            // Ignore close errors (already closed?)
          }
        }
      }
    };

    const handleCanPlay = () => {
      if (!isMounted || !videoRef.current) return;

      // BUGFIX: Prevent re-creating timer worker on tab change
      // canplay event may fire multiple times, but we only need one timer worker
      if (processorRef.current) {
        // Timer worker already exists, just ensure video is playing
        videoRef.current.play().catch(console.warn);
        return;
      }

      // Match canvas size to video dimensions for best quality/performance balance
      // If video dimensions are available, use them. Otherwise fallback to defaults.
      const width = videoRef.current.videoWidth || (isMobileDevice ? 640 : 1280);
      const height = videoRef.current.videoHeight || (isMobileDevice ? 480 : 720);

      if (canvasRef.current) {
        canvasRef.current.width = width;
        canvasRef.current.height = height;
      } else {
        canvasRef.current = createCanvasElement({ width, height });
      }

      // Handle mobile playback with retries
      const playVideoWithRetry = (attempt = 0) => {
        if (attempt >= maxRetries) {
          console.error('Max play attempts reached');
          return;
        }

        videoRef.current?.play().catch((error) => {
          console.warn(`Video play attempt ${attempt + 1} failed:`, error);
          setTimeout(() => playVideoWithRetry(attempt + 1), 500);
        });
      };

      playVideoWithRetry();

      // TIMER WORKER for background processing
      // Fixes issue where video background freezes when tab is inactive
      const createTimerWorker = (): { worker: Worker; blobUrl: string } => {
        const blob = new Blob(
          [
            `self.onmessage = function(e) {
            if (e.data.type === 'start') {
              self.intervalId = setInterval(function() {
                self.postMessage({ type: 'tick' });
              }, e.data.interval);
            } else if (e.data.type === 'stop') {
              clearInterval(self.intervalId);
            }
          };`,
          ],
          { type: 'application/javascript' }
        );
        const blobUrl = URL.createObjectURL(blob);
        return { worker: new Worker(blobUrl), blobUrl };
      };

      // CPU OPTIMIZATION: Cap animation FPS - backgrounds don't need 30fps
      const effectiveFPS = Math.min(targetFPS, isMobileDevice ? 10 : 15);
      const { worker: timerWorker, blobUrl } = createTimerWorker();
      timerWorkerBlobUrlRef.current = blobUrl; // Store for cleanup
      let pendingFrame = false;

      timerWorker.onmessage = (e) => {
        if (e.data.type === 'tick' && !pendingFrame && videoRef.current && !videoRef.current.paused) {
          pendingFrame = true;
          processVideoFrame().finally(() => {
            pendingFrame = false;
          });
        }
      };

      // Start worker at capped FPS
      timerWorker.postMessage({
        type: 'start',
        interval: 1000 / effectiveFPS,
      });

      console.log('[VB Animation] Started at', effectiveFPS, 'FPS');

      // Store worker for cleanup
      processorRef.current = {
        stop: () => {
          timerWorker.postMessage({ type: 'stop' });
          timerWorker.terminate();
          // MEMORY: Revoke blob URL to prevent memory leak
          if (timerWorkerBlobUrlRef.current) {
            URL.revokeObjectURL(timerWorkerBlobUrlRef.current);
            timerWorkerBlobUrlRef.current = null;
          }
        },
      };
    };

    const handleError = () => {
      console.error('Video loading error:', videoRef.current?.error);
      if (isMounted) {
        if (target === 'active') {
          VirtualBackgroundWorker?.setVideoBackgroundFrame(null).catch(console.error);
        } else if (VirtualBackgroundWorker?.setPreviewVideoBackgroundFrame) {
          VirtualBackgroundWorker.setPreviewVideoBackgroundFrame(null).catch(console.error);
        }
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.load();

    return () => {
      isMounted = false;
      activeVideoUrl.current = null;

      if (processorRef.current) {
        processorRef.current.stop();
        processorRef.current = null;
      }

      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);

      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
        videoRef.current.remove();
        videoRef.current = null;
      }

      // MEMORY LEAK FIX: Properly clear canvas and nullify reference for GC
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        canvasRef.current = null; // Allow garbage collection
      }

      if (VirtualBackgroundWorker) {
        if (target === 'active') {
          VirtualBackgroundWorker.setVideoBackgroundFrame(null).catch(console.error);
        } else {
          // Check existence before calling
          if (VirtualBackgroundWorker.setPreviewVideoBackgroundFrame) {
            VirtualBackgroundWorker.setPreviewVideoBackgroundFrame(null).catch(console.error);
          }
        }
      }
    };
  }, [videoUrl, VirtualBackgroundWorker, isMobileDevice, targetFPS, target]);

  return null;
};

export default useVideoBackground;
